import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PageHeader from '../components/PageHeader/PageHeader';
import Label from '../components/Label/Label';
import CustomImageUpload from '../components/CustomImageUpload/CustomImageUpload';
import ClearFileInputIcon from '../components/Icons/ClearFileInputIcon';
import Button from '../components/Button/Button';
import CustomModal from '../components/CustomModal/CustomModal';
import UpgradeAccountIcon from '../components/Icons/UpgradeAccountIcon';
import SubscribeButtons from '../components/Stripe/SubscribeButtons';

interface IWhiteLabelPage {
  props: any;
  logOutModal: (params: boolean) => void;
  editModal: (params: boolean) => void;
  isEditOpen: boolean;
  isFullOpen: boolean;
}
export default function WhiteLabelPage({
  props,
  logOutModal,
  editModal,
  isEditOpen,
  isFullOpen,
}: IWhiteLabelPage) {
  const [leftColor, setLeftColor] = useState(null as string | null);
  const [rightColor, setRightColor] = useState(null as string | null);
  const [backgroundColor, setBackgroundColor] = useState(null as string | null);
  const [imageFile, setImageFile] = useState(null as File | null);
  const [imageURL, setImageURL] = useState(null as string | null);
  const [upgradeFirst, setUpgradeFirst] = useState(false);

  const history = useHistory();

  useEffect(() => {
    async function getWhiteLabelInfo() {
      const whiteLabelInfoResponse = await fetch('/api/Account/WhiteLabelInfo');
      if (!whiteLabelInfoResponse.ok) {
        setUpgradeFirst(true);
      }
      const whiteLabelInfo = await whiteLabelInfoResponse.json();
      setLeftColor(whiteLabelInfo.leftColor);
      setRightColor(whiteLabelInfo.rightColor);
      setBackgroundColor(whiteLabelInfo.backgroundColor);
      setImageURL(whiteLabelInfo.imageURL);
    }
    getWhiteLabelInfo();
  }, []);

  const saveSettings = useCallback(async () => {
    let imageURLToUse = imageURL;
    if (imageFile && !imageURL) {
      let listImageUploadData = new FormData();

      listImageUploadData.append('imageToUpload', imageFile, imageFile.name);

      let listImageUploadResp = await fetch('/api/ImageUpload', {
        method: 'POST',
        body: listImageUploadData,
      });

      let listImageUploadJson = await listImageUploadResp.json();

      imageURLToUse = listImageUploadJson.linkToFile;
    }
    await fetch('/api/Account/WhiteLabelInfo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ leftColor, rightColor, backgroundColor, imageURL: imageURLToUse }),
    });
    history.push('/dashboard');
  }, [leftColor, rightColor, backgroundColor, imageFile, imageURL]);

  return (
    <>
      <PageHeader
        props={props}
        title={'Configure Rumble branding'}
        logOutModal={logOutModal}
        editModal={editModal}
        isEditOpen={isEditOpen}
        isFullOpen={isFullOpen}
      />
      <div className={'container tablet:pt-32'}>
        <div
          className={
            'max-w-[689px] max-tablet:max-w-[335px] mx-auto flex flex-col gap-7'
          }
        >
          <div>
            <Label text={'COMPANY LOGO'} htmlFor={'rumbleImage'} />
            <div className={'flex gap-4'}>
              <div
                className={
                  'relative w-[235px] h-[175px] max-tablet:h-40 flex justify-center'
                }
              >
                {(imageFile || imageURL) && (
                  <button
                    className={
                      'h-6 w-6 flex items-center justify-center bg-primary/[0.75] rounded-full absolute top-2 right-2 z-10'
                    }
                    onClick={() => {
                      setImageFile(null);
                      setImageURL(null);
                    }}
                    title='Remove image'
                  >
                    <ClearFileInputIcon />
                  </button>
                )}
                {imageFile ? (
                  <img
                    className={'max-h-full w-auto object-contain'}
                    src={URL.createObjectURL(imageFile)}
                    alt='Your company logo'
                  />
                ) : imageURL ? (
                  <img
                    className={'max-h-full w-auto object-contain'}
                    src={imageURL}
                    alt='Your company logo'
                  />
                ) : (
                  <img
                    className={'max-h-full w-auto object-contain'}
                    src='/img/rr-logo.png'
                    alt='RumbleRank logo'
                  />
                )}
              </div>
              <div className={'flex-grow'}>
                <CustomImageUpload
                  id={'rumbleImage'}
                  onChange={(e) => {
                    if (e.length) {
                      setImageFile(e[0]);
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className={'grid grid-cols-2 gap-4'}>
            <div>
              <Label text={'LEFT COLOR'} htmlFor={'leftColorPicker'} />
              <div className={'flex flex-col gap-3'}>
                <input
                  type='color'
                  id='leftColorPicker'
                  value={leftColor ? leftColor : '#db3639'}
                  onChange={(evt) => setLeftColor(evt.target.value)}
                  className={'block w-full h-[200px] p-0'}
                />
                <Button
                  variant={'primary'}
                  onClick={() =>
                    document.getElementById('leftColorPicker')!.click()
                  }
                >
                  Choose left color
                </Button>
                <Button
                  variant={'secondary'}
                  onClick={() => setLeftColor(null)}
                >
                  Reset
                </Button>
              </div>
            </div>
            <div>
              <Label text={'RIGHT COLOR'} htmlFor={'rightColorPicker'} />
              <div className={'flex flex-col gap-3'}>
                <input
                  type='color'
                  id='rightColorPicker'
                  value={rightColor ? rightColor : '#3639db'}
                  onChange={(evt) => setRightColor(evt.target.value)}
                  className={'block w-full h-[200px] p-0'}
                />
                <Button
                  variant={'primary'}
                  onClick={() =>
                    document.getElementById('rightColorPicker')!.click()
                  }
                >
                  Choose right color
                </Button>
                <Button
                  variant={'secondary'}
                  onClick={() => setRightColor(null)}
                >
                  Reset
                </Button>
              </div>
            </div>
            <div>
              <Label text={'BACKGROUND COLOR'} htmlFor={'backgroundColorPicker'} />
              <div className={'flex flex-col gap-3'}>
                <input
                  type='color'
                  id='backgroundColorPicker'
                  value={backgroundColor ? backgroundColor : '#0D1333'}
                  onChange={(evt) => setBackgroundColor(evt.target.value)}
                  className={'block w-full h-[200px] p-0'}
                />
                <Button
                  variant={'primary'}
                  onClick={() =>
                    document.getElementById('backgroundColorPicker')!.click()
                  }
                >
                  Choose background color
                </Button>
                <Button
                  variant={'secondary'}
                  onClick={() => setBackgroundColor(null)}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
          <Button onClick={saveSettings} variant={'primary'}>
            Save
          </Button>
        </div>
      </div>
      <CustomModal
        isOpen={upgradeFirst}
        isClose={() => setUpgradeFirst(false)}
        variant={'upgrade'}
        disableClose={upgradeFirst}
      >
        <p className={'text-primary text-16 leading-150 font-semibold mb-3'}>
          Upgrade First!
        </p>
        <p className={'text-primary text-base leading-171 mb-6'}>
          Rumble branding is only available if you have an unlimited account.
          Upgrade your account on the edit profile page before accessing this
          page.
        </p>
        <div className={'flex justify-center mb-6'}>
          <UpgradeAccountIcon />
        </div>
        <div className={'grid grid-cols-2 gap-3'}>
          <Button
            variant={'secondary'}
            onClick={() => {
              history.push('/dashboard');
            }}
          >
            Later
          </Button>
          <SubscribeButtons label={'Upgrade now'} />
        </div>
      </CustomModal>
    </>
  );
}
